import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ApaCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`apa-reveal${visible ? " apa-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4.5L6 21l1.5-7.5L2 9h7z" />
  </svg>
);

const APA_SOURCE_TYPES = [
  { tag: "Website", title: "Website Citation", desc: "Cite webpages, blog posts, and online articles with author, page title, site name, and URL in the right order." },
  { tag: "Book", title: "Book Citation", desc: "Format print books, ebooks, and textbook chapters, including editors, editions, and publisher details." },
  { tag: "Journal Article", title: "Journal Article Citation", desc: "Generate references for scholarly and database articles, complete with volume, issue, and DOI formatting." },
  { tag: "Video", title: "YouTube and Video Citation", desc: "Cite YouTube videos, recorded lectures, and webinars using the uploader name and posting date." },
  { tag: "Newspaper", title: "Newspaper Citation", desc: "Reference print and online news articles with the correct publication name and retrieval details." },
  { tag: "Report", title: "Report Citation", desc: "Cite research reports, white papers, and government publications, including issuing organization." },
];

const APA_STEPS = [
  { title: "Choose the source type", desc: "Select website, book, journal article, or another format so the generator applies the right structure." },
  { title: "Paste the identifier", desc: "Drop in a URL, DOI, ArXiv ID, PubMed ID, or type the title if you don't have a link handy." },
  { title: "Review the pulled details", desc: "Check the author, date, and title the tool found, and correct anything that needs adjusting." },
  { title: "Generate the citation", desc: "Get a full reference entry along with the matching in-text citation, formatted and ready to use." },
  { title: "Copy it into your paper", desc: "Add the reference to your bibliography and drop the in-text citation wherever you use the source." },
];

const APA_EXAMPLES = [
  {
    tag: "Journal Article · APA",
    ref: <>Nguyen, L., &amp; Park, S. (2022). Attention and memory in remote learning environments. <i>Journal of Educational Psychology</i>, 35(1), 55&ndash;72. https://doi.org/10.1017/jep.2022.0055</>,
    intext: "(Nguyen & Park, 2022)",
  },
  {
    tag: "Website · APA",
    ref: <>Bennett, R. (2025, March 4). <i>How citation tools are changing student research habits</i>. ResearchPal Blog. https://researchpal.co/blog/research-habits</>,
    intext: "(Bennett, 2025)",
  },
];

const APA_WHY_ITEMS = [
  { title: "Consistent Formatting", desc: "Every entry follows the same author, date, title, source order, without you tracking the rules manually." },
  { title: "In-Text and Full Reference", desc: "Generates both together, so your citations and reference list always match." },
  { title: "Every Field Is Editable", desc: "Fix an incomplete source detail before you copy the citation into your paper." },
  { title: "Built for Every Source Type", desc: "Websites, books, journal articles, reports, and more, all from one tool." },
  { title: "Free Unlimited APA Citation Generator", desc: "No caps on how many citations you generate and no cost to use the core tool." },
  { title: "Built for Long Bibliographies", desc: "Save entries as you research and export the full list once your paper is ready." },
];

const APA_PLAN_COMPARISON = [
  { feature: "Generate APA citations", free: true, premium: true },
  { feature: "Generate MLA citations", free: true, premium: true },
  { feature: "Generate Chicago citations", free: true, premium: true },
  { feature: "Copy citations", free: true, premium: true },
  { feature: "Save bibliography lists", free: "limited", premium: "unlimited" },
  { feature: "Export to Word or Google Docs", free: "limited", premium: true },
  { feature: "Bulk citation import", free: false, premium: true },
];

const APA_FAQS = [
  { q: "Is this APA citation generator free to use?", a: "Yes. The core tool is completely free, with no limit on how many citations you generate and no account required to use it." },
  { q: "What citation style does this tool follow?", a: "It follows current APA style, covering the author, date, title, and source order that APA reference lists and in-text citations require." },
  { q: "How do I cite a website in APA format?", a: "List the author (or organization) if available, the publication date, the page title in italics, the site name, and the URL, in that order." },
  { q: "How do I cite a book in APA format?", a: "List the author, the year in parentheses, the book title in italics, the edition if it isn't the first, and the publisher name." },
  { q: "How do I cite a journal article in APA format?", a: "Include the author, year, article title, journal name in italics, volume and issue number, page range, and a DOI or URL when one is available." },
  { q: "How many authors can an APA citation include?", a: "APA has separate rules for one author, two authors, and three or more authors, and the reference list can include up to 20 named authors before an ellipsis is used." },
  { q: "How do I cite two authors in APA style?", a: 'Name both authors every time. Use an ampersand in a parenthetical citation, such as (Garcia & Owens, 2023), and the word "and" in a narrative citation.' },
  { q: "What changes when a source has three or more authors?", a: 'Cite only the first author’s surname followed by "et al.", starting with the very first citation, for example (Alvarez et al., 2022).' },
  { q: "What does n.d. mean in an APA citation?", a: 'It stands for "no date" and replaces the publication year when a source doesn’t list when it was published or last updated.' },
  { q: "How do I cite a source with no publication date?", a: "Use n.d. in place of the year, in both the in-text citation and the reference entry, for example (World Health Organization, n.d.)." },
  { q: "Can I generate an in-text citation as well as a full reference?", a: "Yes. Every citation includes both a full reference entry for your bibliography and a matching in-text citation to use in your writing." },
  { q: "Do I need to create an account to use the generator?", a: "No account is required for the free version. You can generate and copy citations right away." },
  { q: "Can I export my citations to Word or Google Docs?", a: "Yes, exporting is available, with the free version offering limited exports and the premium version offering unlimited exports." },
  { q: "Does the generator support DOIs and URLs as input?", a: "Yes. You can paste a DOI, a URL, an ArXiv ID, a PubMed ID, or type in the source details manually if you don't have a link." },
  { q: "How accurate are the citations it creates?", a: "The generator applies current APA formatting rules consistently, though it's always worth a quick check against your source, especially for unusual formats." },
  { q: "What's the difference between the free and premium version?", a: "The free version covers unlimited citation generation. Premium adds unlimited saved bibliographies, full export options, and bulk citation import." },
  { q: "Can I save a bibliography for a longer research paper?", a: "Yes. You can save citations as you research and build a full reference list over time rather than generating everything at once." },
  { q: "Does the tool follow the current APA edition?", a: "Yes, the formatting rules are kept aligned with the current edition of the APA style guide." },
  { q: "Can I edit a citation after it's generated?", a: "Yes. Every field can be adjusted before you copy the citation, so you can fix an incomplete or incorrect detail." },
  { q: "Is my source information kept private?", a: "The details you enter are used only to build your citation and are not shared or published anywhere." },
];

const FAQ_BATCH = 5;

const ApaFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
    setRevealed((prev) => (prev < APA_FAQS.length ? Math.min(prev + FAQ_BATCH, APA_FAQS.length) : prev));
  };

  const visible = APA_FAQS.slice(0, revealed);

  return (
    <>
      <div className="apa-faq-list">
        {visible.map((item, idx) => {
          const open = openSet.has(idx);
          return (
            <div className={`apa-faq-item${open ? " apa-open" : ""}`} key={item.q}>
              <button type="button" className="apa-faq-q" aria-expanded={open} onClick={() => toggle(idx)}>
                <span>{item.q}</span>
                <span className="apa-plus" />
              </button>
              <div className="apa-faq-a" aria-hidden={!open}>
                <p>{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="apa-faq-more-hint">
        {revealed < APA_FAQS.length
          ? `Showing ${revealed} of ${APA_FAQS.length} questions. Click any question to see more.`
          : `Showing all ${APA_FAQS.length} questions.`}
      </p>
    </>
  );
};

const ApaCitationContent = () => {
  return (
    <div className="apa-lp">
      <section className="apa-section">
        <div className="apa-container">
          <Reveal className="apa-intro-grid">
            <span className="apa-eyebrow apa-center">Free APA Format Citation Generator</span>
            <h1>Free APA Citation Generator: Reference + In-Text Citation</h1>
            <p>Every APA reference has to carry the same details in the same order: who wrote it, when it was published, what it's called, and where it can be found. Getting one part wrong (a missing italic, a comma in the wrong place, a reversed author name) is enough to cost points on an assignment that otherwise took hours to write.</p>
            <p>This APA generator handles that formatting for you. Paste in a link, a DOI, or the title of a source, and it builds a reference entry and a matching in-text citation using current APA style rules. There is no limit on how many citations you create, and nothing to install or sign up for.</p>
            <div className="apa-intro-actions">
              <a href="#citation-tool" className="apa-btn apa-btn-primary">Generate My Citation Free</a>
              <a href="#apa-examples" className="apa-btn apa-btn-ghost">See Example Citations</a>
            </div>
          </Reveal>

          <Reveal className="apa-chip-row">
            <span className="apa-chip"><CheckIcon />Faster than manual formatting</span>
            <span className="apa-chip"><CheckIcon />Covers every APA source type</span>
            <span className="apa-chip"><CheckIcon />Builds in-text citations too</span>
            <span className="apa-chip"><CheckIcon />Free and unlimited to use</span>
            <span className="apa-chip"><CheckIcon />Edit any field before you copy</span>
          </Reveal>
        </div>
      </section>

      <section className="apa-section apa-section-soft">
        <div className="apa-container">
          <Reveal as="p" className="apa-quote-block">An APA citation generator takes the raw details of a source (its author, date, title, and publisher) and arranges them into the exact structure the APA style guide requires, so the writer doesn't have to memorize the rules.</Reveal>

          <Reveal className="apa-two-col">
            <div className="apa-info-card">
              <div className="apa-icon-badge"><StackIcon /></div>
              <h3>How It Works</h3>
              <p>Instead of arranging every detail by hand (author order, italics, punctuation), you enter the source information once and the generator formats it according to APA rules. It also builds the matching parenthetical or narrative in-text citation, so both pieces stay consistent.</p>
            </div>
            <div className="apa-info-card">
              <div className="apa-icon-badge"><StarIcon /></div>
              <h3>Why It Helps</h3>
              <p>A long reference list is where small errors add up fastest. Automating the format keeps every entry consistent, cuts down on the back-and-forth of checking a style guide, and leaves a reference page that reads as if a single careful writer built it from start to finish.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="apa-section" id="apa-sources">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "44px" }}>
            <span className="apa-eyebrow apa-center">Pick A Source Type</span>
            <h2 style={{ marginTop: "10px" }}>Build APA Citations for Any Source</h2>
            <p className="apa-lede">Whatever you are citing, this APA style citation generator adapts the reference structure to match it.</p>
          </Reveal>

          <Reveal className="apa-grid-3">
            {APA_SOURCE_TYPES.map((item) => (
              <div className="apa-source-card" key={item.tag}>
                <span className="apa-tag">{item.tag}</span>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="apa-section apa-section-soft">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "40px" }}>
            <span className="apa-eyebrow apa-center">Simple Steps</span>
            <h2 style={{ marginTop: "10px" }}>How This APA Citation Generator Works</h2>
          </Reveal>

          <Reveal className="apa-steps">
            {APA_STEPS.map((step, idx) => (
              <div className="apa-step" key={step.title}>
                <div className="apa-step-num">{idx + 1}</div>
                <div><h4>{step.title}</h4><p>{step.desc}</p></div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="apa-section" id="apa-authors">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "44px" }}>
            <span className="apa-eyebrow apa-center">APA Citation Multiple Authors</span>
            <h2 style={{ marginTop: "10px" }}>How to Cite Two or More Authors in APA</h2>
            <p className="apa-lede">The way authors appear in an APA citation changes depending on how many there are. Here is exactly how each case is formatted.</p>
          </Reveal>

          <Reveal className="apa-rules-wrap">
            <div className="apa-rule-list">
              <div className="apa-rule-item">
                <div className="apa-rule-count">01</div>
                <div><h4>One author</h4><p>Use the surname followed by the year. Parenthetical: <code>(Reyes, 2021)</code>. Narrative: Reyes (2021) argued that...</p></div>
              </div>
              <div className="apa-rule-item">
                <div className="apa-rule-count">02</div>
                <div><h4>Two authors in APA citation</h4><p>Name both authors every single time you cite the source, in the same order as the original. Use an ampersand in a parenthetical citation, <code>(Garcia &amp; Owens, 2023)</code>, and the word "and" in a narrative citation: Garcia and Owens (2023) found...</p></div>
              </div>
              <div className="apa-rule-item">
                <div className="apa-rule-count">03</div>
                <div><h4>Three or more authors</h4><p>Cite only the first author's surname followed by "et al.", starting with the very first time you reference the source: <code>(Alvarez et al., 2022)</code>.</p></div>
              </div>
              <div className="apa-rule-item">
                <div className="apa-rule-count">04</div>
                <div><h4>Reference list entries</h4><p>List up to 20 authors by surname and initials, separated by commas, with an ampersand before the final name. For 21 or more authors, list the first 19, add an ellipsis, then the last author's name.</p></div>
              </div>
            </div>

            <div className="apa-example-panel">
              <span className="apa-eyebrow">Two Authors, Worked Example</span>
              <h4>Reference List Entry</h4>
              <p className="apa-ref">Garcia, M., &amp; Owens, T. (2023). Reading habits among first-year college students. <i>Journal of Higher Education Research</i>, 41(2), 118&ndash;134. https://doi.org/10.1234/jher.2023.0412</p>
              <div className="apa-intext"><b>In-text (parenthetical):</b> (Garcia &amp; Owens, 2023)<br /><br /><b>In-text (narrative):</b> Garcia and Owens (2023) reported...</div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="apa-section apa-section-soft" id="apa-no-date">
        <div className="apa-container">
          <Reveal className="apa-nd-wrap">
            <div className="apa-nd-visual">
              <div className="apa-big-nd">n.d.</div>
              <p className="apa-cap">Placed exactly where the publication year would go</p>
            </div>
            <div className="apa-nd-copy">
              <span className="apa-eyebrow">What Does n.d. Mean in APA</span>
              <h2 style={{ margin: "10px 0 16px", fontSize: "clamp(24px,3vw,30px)" }}>Citing a Source With No Publication Date</h2>
              <p>The abbreviation n.d. stands for "no date." APA style uses it in place of a year whenever a source doesn't list when it was published or last updated, which happens often with web pages, social media posts, and some organizational content.</p>
              <p>It goes in the same spot a year normally would, both in the reference list entry and in the in-text citation.</p>
              <div className="apa-ex-line"><b>In-text:</b> (World Health Organization, n.d.)</div>
              <div className="apa-ex-line"><b>Reference:</b> World Health Organization. (n.d.). <i>Understanding vaccine safety</i>. https://www.who.int/vaccine-safety</div>
              <p>If the page content is likely to change over time, add a retrieval date: "Retrieved [date], from https://..." right after the URL.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="apa-section" id="apa-examples">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "40px" }}>
            <span className="apa-eyebrow apa-center">See It in Action</span>
            <h2 style={{ marginTop: "10px" }}>Example APA Citations</h2>
          </Reveal>

          <Reveal className="apa-grid-2">
            {APA_EXAMPLES.map((ex) => (
              <div className="apa-example-card" key={ex.tag}>
                <div className="apa-tag-row"><span className="apa-tag">{ex.tag}</span></div>
                <p className="apa-ref">{ex.ref}</p>
                <p className="apa-intext"><b>In-text citation:</b> {ex.intext}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="apa-section apa-section-soft">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "44px" }}>
            <span className="apa-eyebrow apa-center">The Payoff</span>
            <h2 style={{ marginTop: "10px" }}>Why This Is the Best APA Citation Generator for Coursework</h2>
          </Reveal>

          <Reveal className="apa-why-grid">
            {APA_WHY_ITEMS.map((w) => (
              <div className="apa-why-card" key={w.title}><h4>{w.title}</h4><p>{w.desc}</p></div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="apa-section">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "24px" }}>
            <span className="apa-eyebrow apa-center" style={{ color: '#10b981', fontWeight: 700, letterSpacing: '0.12em' }}>COMPARE PLANS</span>
            <h2 style={{ marginTop: "10px", fontFamily: 'var(--apa-serif)' }}>Free and Premium Features</h2>
          </Reveal>

          <Reveal className="apa-compare-wrap">
            <table className="apa-compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free Version</th>
                  <th>Premium Version</th>
                </tr>
              </thead>
              <tbody>
                {APA_PLAN_COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>
                      {row.free === true ? (
                        <span className="apa-yes">✓</span>
                      ) : row.free === false ? (
                        <span className="apa-no">✕</span>
                      ) : row.free === "limited" ? (
                        <span className="apa-pill-limited">Limited</span>
                      ) : null}
                    </td>
                    <td>
                      {row.premium === true ? (
                        <span className="apa-yes">✓</span>
                      ) : row.premium === "unlimited" ? (
                        <span className="apa-pill-unlimited">Unlimited</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="apa-section" id="apa-ai-research-assistant">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "36px" }}>
            <span className="apa-eyebrow apa-center">AI Research Assistant</span>
            <h2 style={{ marginTop: "10px" }}>AI Research Assistant for APA Research and Referencing</h2>
          </Reveal>

          <Reveal className="apa-grid-2">
            <div className="apa-example-card">
              <p style={{ marginBottom: "14px" }}>APA referencing is frequently used across psychology, education, social sciences, business and other research disciplines. However, formatting the reference is only one part of using sources effectively.</p>
              <p style={{ marginBottom: "14px" }}>With ResearchPal's AI Research Assistant, you can discuss the research behind your APA citations before adding those references to your paper. The Agent can help you understand a source, clarify academic terminology, explore how evidence relates to your topic, and work through research questions that arise while writing.</p>
              <p>ResearchPal's citation system supports APA references alongside other citation formats and can work from source information such as URLs, DOIs and research identifiers.</p>
            </div>
            <div className="apa-example-card">
              <h4 style={{ marginBottom: "10px" }}>Combine APA Citations With Research Assistance</h4>
              <p style={{ marginBottom: "12px", fontSize: "14px", color: "var(--apa-ink-soft)" }}>Use the AI Research Assistant to:</p>
              <ul className="apa-ai-list">
                <li>Understand a source before citing it</li>
                <li>Discuss how evidence supports your argument</li>
                <li>Clarify APA-related research questions</li>
                <li>Explore a topic before building your reference list</li>
                <li>Connect academic sources with your research question</li>
                <li>Refine ideas before adding them to your paper</li>
              </ul>
            </div>
          </Reveal>

          <Reveal className="apa-center" style={{ marginTop: "30px" }}>
            <Link to="/ai-agent" className="apa-btn apa-btn-primary">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="apa-section apa-section-soft" id="apa-free-tools">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "40px" }}>
            <span className="apa-eyebrow apa-center">More Free Tools</span>
            <h2 style={{ marginTop: "10px" }}>Explore ResearchPal's Free Tools</h2>
          </Reveal>

          <div className="apa-free-tools-group">
            <h3 className="apa-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="apa-grid-3">
              <Link className="apa-free-tool-card" to="/paraphraser">
                <div><h4>Paraphraser</h4><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/ai-agent">
                <div><h4>AI Agent</h4><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/search">
                <div><h4>Search Papers</h4><p>Find and explore academic papers across sources.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/essay-writer">
                <div><h4>Essay Writer</h4><p>Draft structured essays with AI assistance.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/literature-review">
                <div><h4>Literature Review</h4><p>Summarize and organize sources for your review.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/paper-insights">
                <div><h4>Paper Insights</h4><p>Get quick, structured insights from any paper.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="apa-free-tools-group">
            <h3 className="apa-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="apa-grid-3">
              <Link className="apa-free-tool-card" to="/citation-generator/harvard">
                <div><h4>Harvard Citation Generator</h4><p>Create author-date Harvard references.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/citation-generator/mla">
                <div><h4>MLA Citation Generator</h4><p>Generate MLA in-text and Works Cited entries.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/citation-generator/chicago">
                <div><h4>Chicago Citation Generator</h4><p>Build Notes-Bibliography or Author-Date citations.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/citation-generator/bibtex">
                <div><h4>BibTeX Generator</h4><p>Get ready-to-use BibTeX entries for LaTeX.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
              <Link className="apa-free-tool-card" to="/citation-generator/metadata">
                <div><h4>Metadata Lookup</h4><p>View a source's structured details before citing it.</p></div>
                <span className="apa-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="apa-section">
        <div className="apa-container">
          <Reveal className="apa-cta-banner">
            <h2>Ready to Generate Your APA Citation?</h2>
            <p>Paste a link, DOI, ArXiv ID, PubMed ID, or title and get a formatted APA reference in seconds. Free to use, with no sign-up required.</p>
            <a href="#citation-tool" className="apa-btn apa-btn-primary">Generate My Citation Free</a>
          </Reveal>
        </div>
      </section>

      <section className="apa-section" id="apa-faq">
        <div className="apa-container">
          <Reveal className="apa-center" style={{ marginBottom: "40px" }}>
            <span className="apa-eyebrow apa-center">Questions and Answers</span>
            <h2 style={{ marginTop: "10px" }}>Frequently Asked Questions</h2>
            <p className="apa-lede">Click any question to see the answer, and to reveal more questions below.</p>
          </Reveal>

          <ApaFaqList />
        </div>
      </section>
    </div>
  );
};

export default ApaCitationContent;
